import { type Defineable } from "./types/utility";

// ── Date / time helpers ─────────────────────────────────────────────────────────

/**
 * Formats a date as a time string for today, or "Mon D <time>" for older dates.
 * Defaults to "h:mm AM/PM" when no timeOptions are provided.
 */
export const formatDate = (
    date: Date,
    timeOptions: Intl.DateTimeFormatOptions = {
        hour: "numeric",
        minute: "2-digit",
    }
): string => {
    const time = date.toLocaleTimeString([], timeOptions);
    if (date.toDateString() === new Date().toDateString()) return time;
    return `${date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
    })} ${time}`;
};

/**
 * Formats an ISO timestamp as a localized relative time ("in 5 minutes",
 * "2 hours ago"), choosing minute/hour/day units by distance from now.
 *
 * Throws a RangeError on a timestamp it cannot parse, by way of
 * `Intl.RelativeTimeFormat`. That is the right answer for a caller holding a
 * timestamp it believes in; a caller rendering an optional one from an API
 * wants `formatRelativeSafe` instead.
 */
export const formatRelative = (iso: string): string => {
    const diffMs = new Date(iso).getTime() - Date.now();
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
    const abs = Math.abs(diffMs);
    if (abs < 3_600_000)
        return rtf.format(Math.round(diffMs / 60_000), "minute");
    if (abs < 86_400_000)
        return rtf.format(Math.round(diffMs / 3_600_000), "hour");
    return rtf.format(Math.round(diffMs / 86_400_000), "day");
};

/**
 * `formatRelative` for a timestamp that may be missing or unparseable, which
 * answers undefined instead of throwing.
 *
 * Undefined rather than a placeholder string because the caller knows what an
 * absent age should look like next to the rest of its line, and this does not.
 * Separate from `formatRelative` rather than widening it so a caller that means
 * to pass a real timestamp still fails loudly when it turns out not to have one.
 */
export const formatRelativeSafe = (iso?: string): Defineable<string> => {
    if (!iso || !Number.isFinite(new Date(iso).getTime())) return undefined;
    return formatRelative(iso);
};

/**
 * Formats an ISO timestamp as a short "D Mon HH:MM" (no weekday, no year), or
 * undefined when the input is missing or unparseable.
 *
 * Unparseable counts as missing, for the same reason `formatRelativeSafe`
 * exists: `toLocaleString` answers a bad date with the literal string "Invalid
 * Date", which renders beside a responder's name as "until Invalid Date".
 */
export const formatShiftEnd = (iso?: string): Defineable<string> => {
    if (!iso || !Number.isFinite(new Date(iso).getTime())) return undefined;
    return new Date(iso).toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    });
};

// ── Entity ref helpers ─────────────────────────────────────────────────────────

/**
 * Returns a cleaned group name, or undefined if the ref is not a group.
 * Strips the leading "group:default/" prefix and the "jha-psa-backstage-" org prefix.
 *
 * @example
 *   cleanGroupName("group:default/jha-psa-backstage-payments") // "payments"
 *   cleanGroupName("user:default/alice")                        // undefined
 */
export const cleanGroupName = (ref: string): Defineable<string> => {
    if (!ref.startsWith("group:")) return undefined;
    return (ref.split("/").pop() ?? ref)
        .replace(/^group:/i, "")
        .replace(/^jha-psa-backstage-/i, "");
};
